import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Subscription } from "rxjs";

import {
  CODIGOS_PRUEBAS,
  CODIGOS_VERSIONES,
  CiclosPorTipo,
  Ejecutor,
  EjecucionPrueba,
  EjecucionPruebaRespuesta,
  EvidenciaPrueba,
  IncidenciaDeEtapa,
  IncidenciaVersion,
  LARGO_OBSERVACIONES_PRUEBA,
  MAXIMO_EVIDENCIAS_PRUEBA,
  ResultadoPrueba,
  TipoPrueba,
  VersionResumen,
} from "../etapas-versiones.model";
import { EtapasVersionesService } from "../etapas-versiones.service";
import { fechaCorta, versionesRecientesPrimero } from "../etapas-versiones.util";
import { abrirIncidenciaRapida } from "../incidencia-rapida/incidencia-rapida.component";
import { IncidenciasDeLaConsulta, clasePrioridad, nombrePrioridad } from "../incidencias-prueba.util";
import { cierrePorTeclaOFondo, configDialogoPruebas } from "../pruebas-dialogo";
import {
  ACEPTA_EVIDENCIA,
  CampoEjecucion,
  ErroresEjecucion,
  LimitesFecha,
  RESULTADOS_PRUEBA,
  TIPOS_PRUEBA,
  ValoresEjecucion,
  diaDeFechaLocal,
  fechaLocalDeDia,
  limitesDeFecha,
  lineaArchivo,
  nombreCiclo,
  nombreDeEvidencia,
  nombreTipoPrueba,
  peticionEjecucion,
  revisarEvidencias,
  textoLimitesFecha,
  textoRegistroEjecucion,
  textoSeRegistraComo,
  validarEjecucion,
} from "../pruebas-version.util";
import { abrirVincularIncidencia } from "../vincular-incidencia/vincular-incidencia.component";
import { descargarEvidencia } from "../descarga-evidencia";

/** Lo que recibe T2: la etapa, las versiones que se pueden probar y quién ejecuta; con ejecucion, edita. */
export interface DatosRegistrarEjecucion {
  idProyecto: string;
  idEtapa: string;
  nombreProyecto?: string | null;
  nombreEtapa?: string | null;
  /** Desde A1, todas las de la etapa; desde A2, la de la página. */
  versiones: readonly VersionResumen[];
  /** Si versiones trae todas las de la etapa (para nombrar la versión detectada de cada incidencia). */
  todasLasVersiones: boolean;
  /** La que sale elegida; sin ella, la vigente. */
  idVersion?: string | null;
  ejecutores: readonly Ejecutor[];
  siguienteCiclo: CiclosPorTipo | null;
  /** D4 con los recursos de la etapa y D8; el servidor valida igual. */
  puedeRegistrarPruebas: boolean;
  /** «Editar ejecución» (desde A2): no cambian versión, tipo ni ciclo (D5). */
  ejecucion?: EjecucionPrueba | null;
  /** Incidencias que ya se conocen (las de A2), para nombrar las ligadas sin consultar. */
  conocidas?: readonly IncidenciaVersion[] | null;
}

/**
 * Lo que da afterClosed(): la ejecución guardada, o null si se cerró sin
 * guardar pero algo cambió (se crearon incidencias o no se supo si llegó el
 * guardado). undefined = se canceló sin cambios.
 */
export interface ResultadoRegistrarEjecucion {
  ejecucion: EjecucionPrueba | null;
  /** Las que se crearon desde «Crear incidencia»; existen aunque la ejecución no se guarde. */
  incidenciasCreadas: number;
}

/** Una versión en el selector: «V3-13-08-2026 · Vigente». */
interface OpcionVersion {
  id: string;
  etiqueta: string;
  version: VersionResumen;
}

/** Una evidencia ya guardada (edición), marcada si se va a quitar. */
interface EvidenciaGuardada {
  evidencia: EvidenciaPrueba;
  nombre: string;
  linea: string;
  quitar: boolean;
}

interface ControlesEjecucion {
  idVersion: FormControl<string | null>;
  tipo: FormControl<TipoPrueba | null>;
  fecha: FormControl<Date | null>;
  idEmpleadoEjecuto: FormControl<number | null>;
  resultado: FormControl<ResultadoPrueba | null>;
  casosEjecutados: FormControl<number | null>;
  casosAprobados: FormControl<number | null>;
  casosFallidos: FormControl<number | null>;
  observaciones: FormControl<string>;
}

const ID_TITULO = "t2-titulo";
const SIN_PERMISO =
  "Registran pruebas los líderes del proyecto y de la etapa, quien tenga permiso de proyectos y los recursos de la etapa (D4); en un proyecto cerrado ya no se registran.";

/** Abre «Registrar ejecución de pruebas» (T2), o su edición si trae la ejecución. */
export function abrirRegistrarEjecucion(
  dialog: MatDialog,
  datos: DatosRegistrarEjecucion,
): MatDialogRef<RegistrarEjecucionComponent, ResultadoRegistrarEjecucion> {
  return dialog.open<RegistrarEjecucionComponent, DatosRegistrarEjecucion, ResultadoRegistrarEjecucion>(
    RegistrarEjecucionComponent,
    configDialogoPruebas(datos, ID_TITULO, "880px"),
  );
}

/**
 * Gestión de Proyectos · «Registrar ejecución de pruebas» (T2; plan
 * docs/superpowers/plans/2026-10-13-etapas-y-versiones.md §5.7 y §8,
 * maquetas T2 y T2e). Guarda con PROYECTOS_PRUEBAS_GUARDAR en una sola
 * petición multipart con la evidencia.
 *
 * - Alta: cualquier versión de la etapa (la vigente por omisión; las
 *   cerradas también se prueban); el ciclo lo asigna el servidor y aquí solo
 *   se anuncia («Se registrará como «Regresión 2»», D16).
 * - Edición (desde A2): versión, tipo y ciclo no cambian (D5); las
 *   evidencias que se quitan salen de la ejecución y su archivo se queda en
 *   el servidor.
 * - Las reglas son las de ReglasEjecucionPrueba (validarEjecucion): se
 *   avisan en el campo antes de mandar nada, y el servidor valida igual.
 * - Incidencias levantadas: «Vincular incidencia existente» (T2v) y «Crear
 *   incidencia» (T2i, que la crea al momento con la versión probada).
 */
@Component({
  selector: "app-registrar-ejecucion",
  standalone: true,
  templateUrl: "./registrar-ejecucion.component.html",
  styleUrls: ["../pruebas-dialogo.css", "./registrar-ejecucion.component.css"],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
  ],
})
export class RegistrarEjecucionComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly tipos = TIPOS_PRUEBA;
  readonly resultados = RESULTADOS_PRUEBA;
  readonly largoObservaciones = LARGO_OBSERVACIONES_PRUEBA;
  readonly maximoEvidencias = MAXIMO_EVIDENCIAS_PRUEBA;
  readonly acepta = ACEPTA_EVIDENCIA;
  readonly sinPermiso = SIN_PERMISO;
  readonly esEdicion: boolean;
  readonly opcionesVersion: OpcionVersion[];
  readonly ejecutores: Ejecutor[];
  readonly hoy: Date;

  /** Entre qué días cae la fecha de ejecución; cambian con la versión elegida (actualizarLimites). */
  limites: LimitesFecha;
  textoFechas = "";
  fechaMinima: Date | null = null;

  readonly formulario = new FormGroup<ControlesEjecucion>({
    idVersion: new FormControl<string | null>(null, { validators: [this.regla("idVersion")] }),
    tipo: new FormControl<TipoPrueba | null>(null, { validators: [this.regla("tipo")] }),
    fecha: new FormControl<Date | null>(null, { validators: [this.regla("fechaEjecucion")] }),
    idEmpleadoEjecuto: new FormControl<number | null>(null, { validators: [this.regla("idEmpleadoEjecuto")] }),
    resultado: new FormControl<ResultadoPrueba | null>(null, { validators: [this.regla("resultado")] }),
    casosEjecutados: new FormControl<number | null>(null, { validators: [this.regla("casosEjecutados")] }),
    casosAprobados: new FormControl<number | null>(null, { validators: [this.regla("casosAprobados")] }),
    casosFallidos: new FormControl<number | null>(null, { validators: [this.regla("casosFallidos")] }),
    observaciones: new FormControl("", { nonNullable: true, validators: [this.regla("observaciones")] }),
  });

  /** Las incidencias levantadas, en el orden en que se ligaron. */
  vinculadas: IncidenciaDeEtapa[] = [];
  /** Las evidencias que ya tiene la ejecución (edición). */
  guardadas: EvidenciaGuardada[] = [];
  /** Los archivos nuevos que se suben al guardar. */
  nuevos: File[] = [];
  avisoArchivos = "";
  /** Avisos que no detienen nada (la evidencia de una incidencia creada que no subió). */
  avisos: string[] = [];
  guardando = false;
  /** Ya se intentó guardar: se muestran las reglas entre campos. */
  intentado = false;
  mensaje = "";

  private readonly conocidas = new Map<number, IncidenciaDeEtapa>();
  private consultaEtapa: IncidenciasDeLaConsulta | null = null;
  private incidenciasCreadas = 0;
  /** No se supo si el guardado llegó: al cerrar, quien abrió vuelve a leer. */
  private recargarAlCerrar = false;
  private cerrado = false;
  private readonly subs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosRegistrarEjecucion,
    private readonly dialogRef: MatDialogRef<RegistrarEjecucionComponent, ResultadoRegistrarEjecucion>,
    private readonly servicio: EtapasVersionesService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.hoy = new Date();
    this.limites = limitesDeFecha(null, this.hoy);
    this.esEdicion = !!datos?.ejecucion?.id;
    this.opcionesVersion = versionesRecientesPrimero(datos?.versiones ?? [])
      .filter((version) => !!version.id)
      .map((version) => ({ id: version.id as string, etiqueta: etiquetaVersion(version), version }));
    this.ejecutores = ejecutoresConElGuardado(datos?.ejecutores ?? [], datos?.ejecucion ?? null);
    (datos?.conocidas ?? []).forEach((incidencia) => this.conocidas.set(incidencia.idIncidencia, deIncidenciaVersion(incidencia)));
  }

  ngOnInit(): void {
    this.subs.push(
      ...cierrePorTeclaOFondo(this.dialogRef, () => this.cancelar()),
      this.formulario.valueChanges.subscribe(() => {
        this.actualizarLimites();
        this.revalidar();
      }),
    );
    this.llenar();
    if (!this.datos.puedeRegistrarPruebas) {
      this.formulario.disable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  get editable(): boolean {
    return this.datos.puedeRegistrarPruebas && !this.guardando;
  }

  /** «Registrar ejecución de pruebas» o «Editar ejecución · Regresión 2». */
  get titulo(): string {
    const ejecucion = this.datos.ejecucion;
    return this.esEdicion && ejecucion ? `Editar ejecución · ${nombreCiclo(ejecucion.tipo, ejecucion.ciclo)}` : "Registrar ejecución de pruebas";
  }

  get subtitulo(): string {
    const version = this.esEdicion ? this.versionElegida?.nombre : null;
    return [this.datos.nombreProyecto, this.datos.nombreEtapa, version].map((p) => (p ?? "").trim()).filter(Boolean).join(" · ");
  }

  get textoGuardar(): string {
    if (this.guardando) {
      return "Guardando…";
    }
    return this.esEdicion ? "Guardar cambios" : "Guardar ejecución";
  }

  get lineaRegistro(): string | null {
    return this.datos.ejecucion ? textoRegistroEjecucion(this.datos.ejecucion) : null;
  }

  get versionElegida(): VersionResumen | null {
    const id = this.formulario.controls.idVersion.value;
    return this.opcionesVersion.find((opcion) => opcion.id === id)?.version ?? null;
  }

  get etiquetaVersionElegida(): string {
    const version = this.versionElegida;
    return version ? etiquetaVersion(version) : "—";
  }

  get ambiente(): string {
    return this.versionElegida?.ambiente?.trim() || "—";
  }

  get tipoGuardado(): string {
    return nombreTipoPrueba(this.datos.ejecucion?.tipo);
  }

  /** «Se registrará como «Regresión 2»» (alta). */
  get textoCiclo(): string | null {
    return textoSeRegistraComo(this.datos.siguienteCiclo, this.formulario.controls.tipo.value);
  }

  get errores(): ErroresEjecucion {
    return validarEjecucion(this.valores(), this.limites);
  }

  /** La regla entre casos y resultado, cuando ya se tocaron los casos o se intentó guardar. */
  get errorCasos(): string | null {
    const { casosEjecutados, casosAprobados, casosFallidos } = this.formulario.controls;
    const tocados = this.intentado || casosEjecutados.touched || casosAprobados.touched || casosFallidos.touched;
    return tocados ? (this.errores.casos ?? null) : null;
  }

  get totalEvidencias(): number {
    return this.guardadas.filter((g) => !g.quitar).length + this.nuevos.length;
  }

  /** El mensaje de la regla del campo (para mat-error). */
  errorDe(control: keyof ControlesEjecucion): string | null {
    const errores = this.formulario.controls[control].errors;
    return typeof errores?.["regla"] === "string" ? errores["regla"] : null;
  }

  /** Para los campos sin mat-form-field (resultado): el error si ya se tocó o se intentó guardar. */
  errorVisible(control: keyof ControlesEjecucion): string | null {
    const c = this.formulario.controls[control];
    return c.touched || this.intentado ? this.errorDe(control) : null;
  }

  clasePrioridad(prioridad: string | null): string {
    return clasePrioridad(prioridad);
  }

  nombrePrioridad(prioridad: string | null): string {
    return nombrePrioridad(prioridad);
  }

  lineaArchivo(archivo: File): string {
    return lineaArchivo(archivo.name, archivo.size);
  }

  trackVinculada(_: number, incidencia: IncidenciaDeEtapa): number {
    return incidencia.id;
  }

  /* ============================ Incidencias levantadas ============================ */

  vincularExistente(): void {
    const idVersion = this.formulario.controls.idVersion.value;
    if (!this.editable) {
      return;
    }
    abrirVincularIncidencia(this.dialog, {
      idProyecto: this.datos.idProyecto,
      idEtapa: this.datos.idEtapa,
      idVersion,
      nombreProyecto: this.datos.nombreProyecto,
      nombreEtapa: this.datos.nombreEtapa,
      contexto: [this.versionElegida?.nombre, this.nombreEjecucion()].filter(Boolean).join(" · "),
      vinculadas: this.vinculadas.map((incidencia) => incidencia.id),
      consulta: this.consultaEtapa,
      nombresVersion: this.nombresVersion(),
      todasLasVersiones: this.datos.todasLasVersiones,
    })
      .afterClosed()
      .subscribe((resultado) => {
        if (resultado?.consulta) {
          this.consultaEtapa = resultado.consulta;
          this.nombrarConLaConsulta();
        }
        resultado?.seleccionadas.forEach((incidencia) => this.vincular(incidencia));
        this.pinta();
      });
  }

  crearIncidencia(): void {
    const idVersion = this.formulario.controls.idVersion.value;
    if (!this.editable || !idVersion) {
      return;
    }
    abrirIncidenciaRapida(this.dialog, {
      idProyecto: this.datos.idProyecto,
      idEtapa: this.datos.idEtapa,
      idVersion,
      nombreProyecto: this.datos.nombreProyecto,
      nombreEtapa: this.datos.nombreEtapa,
      etiquetaVersion: this.etiquetaVersionElegida,
      etiquetaEjecucion: this.nombreEjecucion() ?? "Ejecución nueva",
    })
      .afterClosed()
      .subscribe((resultado) => {
        if (!resultado) {
          return;
        }
        this.incidenciasCreadas++;
        this.consultaEtapa?.incidencias.push(resultado.incidencia);
        this.vincular(resultado.incidencia);
        if (resultado.avisoEvidencia) {
          this.avisos = [...this.avisos, resultado.avisoEvidencia];
        }
        this.pinta();
      });
  }

  quitarIncidencia(incidencia: IncidenciaDeEtapa): void {
    this.vinculadas = this.vinculadas.filter((otra) => otra.id !== incidencia.id);
  }

  /* ============================ Evidencia ============================ */

  elegirArchivos(entrada: HTMLInputElement): void {
    this.agregar(Array.from(entrada.files ?? []));
    entrada.value = "";
  }

  soltar(evento: DragEvent): void {
    evento.preventDefault();
    if (this.editable) {
      this.agregar(Array.from(evento.dataTransfer?.files ?? []));
    }
  }

  permitirSoltar(evento: DragEvent): void {
    evento.preventDefault();
  }

  quitarNuevo(indice: number): void {
    this.nuevos = this.nuevos.filter((_, i) => i !== indice);
    this.avisoArchivos = "";
  }

  /** Quita (o vuelve a dejar) una evidencia guardada; su archivo se queda en el servidor (D5). */
  alternarGuardada(guardada: EvidenciaGuardada): void {
    if (guardada.quitar && this.totalEvidencias >= MAXIMO_EVIDENCIAS_PRUEBA) {
      this.avisoArchivos = `Una ejecución lleva hasta ${MAXIMO_EVIDENCIAS_PRUEBA} archivos de evidencia.`;
      return;
    }
    guardada.quitar = !guardada.quitar;
    this.avisoArchivos = "";
  }

  verEvidencia(guardada: EvidenciaGuardada): void {
    const ejecucion = this.datos.ejecucion;
    if (ejecucion) {
      this.subs.push(
        descargarEvidencia(this.servicio, ejecucion.id, guardada.evidencia, (aviso) => {
          this.avisoArchivos = aviso;
          this.pinta();
        }),
      );
    }
  }

  private agregar(archivos: File[]): void {
    const bytes = this.nuevos.reduce((suma, archivo) => suma + archivo.size, 0);
    const revision = revisarEvidencias(archivos, this.totalEvidencias, bytes);
    this.nuevos = [...this.nuevos, ...revision.aceptados];
    this.avisoArchivos = revision.rechazos.join(" ");
    this.pinta();
  }

  /* ============================ Guardar ============================ */

  guardar(): void {
    if (!this.editable) {
      return;
    }
    this.intentado = true;
    this.formulario.markAllAsTouched();
    this.revalidar();
    if (Object.keys(this.errores).length > 0) {
      this.mensaje = "Revisa los campos marcados.";
      this.pinta();
      return;
    }
    const ejecucion = this.datos.ejecucion;
    const peticion = peticionEjecucion(
      {
        id: ejecucion?.id ?? null,
        idProyecto: this.datos.idProyecto,
        idEtapa: this.datos.idEtapa,
        fechaActualizacion: ejecucion?.fechaActualizacion ?? null,
      },
      this.valores(),
      this.vinculadas.map((incidencia) => incidencia.id),
      this.guardadas.filter((g) => g.quitar).map((g) => g.evidencia.archivo),
    );
    this.guardando = true;
    this.mensaje = "";
    this.formulario.disable({ emitEvent: false });
    this.pinta();
    this.subs.push(
      this.servicio.guardarEjecucion(peticion, this.nuevos).subscribe({
        next: (respuesta) => this.alGuardar(respuesta),
        error: (error: unknown) => this.fallaDeRed(error),
      }),
    );
  }

  private alGuardar(respuesta: EjecucionPruebaRespuesta | null): void {
    if (respuesta?.pCodRetorno === CODIGOS_VERSIONES.EXITO && respuesta.ejecucion) {
      this.guardando = false;
      this.cierra({ ejecucion: respuesta.ejecucion, incidenciasCreadas: this.incidenciasCreadas });
      return;
    }
    if (!respuesta) {
      // Un multipart que pasa el tope del contenedor (500 MB, WebConfig) se
      // rechaza antes del servicio y RestErrorHandler responde 200 sin
      // cuerpo: no se guardó nada. El de 50 MB lo valida el servidor con
      // VAL-PRU-EVIDENCIA.
      this.falla("No se pudo guardar la ejecución: el servidor no respondió. Revisa que los archivos nuevos no pasen de 50 MB juntos.");
      return;
    }
    if (respuesta.pCodRetorno === CODIGOS_PRUEBAS.CONFLICTO) {
      // Otra persona la editó (o tomó el ciclo) mientras se capturaba: lo que
      // tiene la pantalla ya es viejo, así que al cerrar quien abrió vuelve a leer.
      this.recargarAlCerrar = true;
    }
    this.falla(respuesta.pMensaje?.trim() || "No se pudo guardar la ejecución.");
  }

  private fallaDeRed(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 413) {
      this.falla("Los archivos pasan del límite de 50 MB por guardado: quita alguno y vuelve a guardar.");
      return;
    }
    // No se sabe si llegó: un alta repetida registraría otra ejecución.
    this.recargarAlCerrar = true;
    this.falla(
      this.esEdicion
        ? "No se pudo confirmar el guardado: sin conexión con el servidor. Vuelve a guardar."
        : "No se pudo confirmar el guardado: sin conexión con el servidor. Antes de volver a guardar, revisa en el detalle de la versión que no haya quedado registrada.",
    );
  }

  private falla(texto: string): void {
    this.guardando = false;
    this.mensaje = texto;
    this.formulario.enable({ emitEvent: false });
    this.bloquearLoFijo();
    this.pinta();
  }

  /* ============================ Cerrar ============================ */

  cancelar(): void {
    if (this.guardando || this.cerrado) {
      return;
    }
    if (this.incidenciasCreadas > 0 || this.recargarAlCerrar) {
      this.cierra({ ejecucion: null, incidenciasCreadas: this.incidenciasCreadas });
      return;
    }
    this.cerrado = true;
    this.dialogRef.close();
  }

  private cierra(resultado: ResultadoRegistrarEjecucion): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(resultado);
  }

  /* ============================ Internas ============================ */

  /** El valor inicial: la ejecución que se edita o, en un alta, la versión elegida (la vigente por omisión). */
  private llenar(): void {
    const ejecucion = this.datos.ejecucion;
    if (ejecucion && this.esEdicion) {
      this.formulario.patchValue({
        idVersion: ejecucion.idVersion,
        tipo: ejecucion.tipo,
        fecha: fechaLocalDeDia(ejecucion.fechaEjecucion),
        idEmpleadoEjecuto: ejecucion.idEmpleadoEjecuto,
        resultado: ejecucion.resultado,
        casosEjecutados: ejecucion.casosEjecutados,
        casosAprobados: ejecucion.casosAprobados,
        casosFallidos: ejecucion.casosFallidos,
        observaciones: ejecucion.observaciones ?? "",
      });
      this.guardadas = (ejecucion.evidencias ?? []).map((evidencia) => ({
        evidencia,
        nombre: nombreDeEvidencia(evidencia),
        linea: lineaArchivo(nombreDeEvidencia(evidencia), evidencia.tamano),
        quitar: false,
      }));
      this.vinculadas = (ejecucion.incidencias ?? []).map((id) => this.conocidas.get(id) ?? sinNombre(id));
      this.bloquearLoFijo();
      if (this.vinculadas.some((incidencia) => incidencia.titulo === null)) {
        this.leerIncidenciasDeLaEtapa();
      }
      return;
    }
    const pedida = this.opcionesVersion.find((opcion) => opcion.id === this.datos.idVersion);
    const vigente = this.opcionesVersion.find((opcion) => opcion.version.estado === "VIGENTE");
    this.formulario.patchValue({ idVersion: (pedida ?? vigente ?? this.opcionesVersion[0])?.id ?? null });
  }

  /** En la edición, versión y tipo no cambian (D5). */
  private bloquearLoFijo(): void {
    if (this.esEdicion) {
      this.formulario.controls.idVersion.disable({ emitEvent: false });
      this.formulario.controls.tipo.disable({ emitEvent: false });
    }
  }

  /** En la edición, el título de las ligadas que A2 no traía. */
  private leerIncidenciasDeLaEtapa(): void {
    this.subs.push(
      this.servicio.incidenciasDeLaEtapa(this.datos.idProyecto, this.datos.idEtapa).subscribe({
        next: (consulta) => {
          if (!consulta.error) {
            this.consultaEtapa = consulta;
            this.nombrarConLaConsulta();
            this.pinta();
          }
        },
        error: () => {
          // Las ligadas siguen con su id interno; vincular volverá a consultar.
        },
      }),
    );
  }

  private nombrarConLaConsulta(): void {
    this.consultaEtapa?.incidencias.forEach((incidencia) => this.conocidas.set(incidencia.id, incidencia));
    this.vinculadas = this.vinculadas.map((incidencia) => this.conocidas.get(incidencia.id) ?? incidencia);
  }

  private vincular(incidencia: IncidenciaDeEtapa): void {
    this.conocidas.set(incidencia.id, incidencia);
    if (!this.vinculadas.some((otra) => otra.id === incidencia.id)) {
      this.vinculadas = [...this.vinculadas, incidencia];
    }
  }

  /** «Regresión 2 · 14/08/2026» de la ejecución que se edita; null en un alta. */
  private nombreEjecucion(): string | null {
    const ejecucion = this.datos.ejecucion;
    if (!this.esEdicion || !ejecucion) {
      return null;
    }
    return [nombreCiclo(ejecucion.tipo, ejecucion.ciclo), fechaCorta(ejecucion.fechaEjecucion)].filter(Boolean).join(" · ");
  }

  /** El nombre de cada versión que se conoce, por su id (la columna «Versión detectada» de T2v). */
  private nombresVersion(): Record<string, string> {
    const nombres: Record<string, string> = {};
    this.datos.conocidas?.forEach((incidencia) => {
      if (incidencia.detectadaEn?.id && incidencia.detectadaEn.nombre) {
        nombres[incidencia.detectadaEn.id] = incidencia.detectadaEn.nombre;
      }
    });
    this.opcionesVersion.forEach((opcion) => {
      nombres[opcion.id] = opcion.version.nombre?.trim() || "Sin nombre";
    });
    return nombres;
  }

  /** Lo capturado, con la fecha como yyyy-MM-dd (con los controles bloqueados también). */
  private valores(): ValoresEjecucion {
    const valor = this.formulario.getRawValue();
    return {
      idVersion: valor.idVersion,
      tipo: valor.tipo,
      fechaEjecucion: diaDeFechaLocal(valor.fecha),
      idEmpleadoEjecuto: valor.idEmpleadoEjecuto ?? null,
      resultado: valor.resultado,
      casosEjecutados: valor.casosEjecutados ?? null,
      casosAprobados: valor.casosAprobados ?? null,
      casosFallidos: valor.casosFallidos ?? null,
      observaciones: valor.observaciones,
    };
  }

  /**
   * El validador de un control: la regla de validarEjecucion para su campo
   * («casos», la regla entre los tres campos, se pinta aparte). Al construir
   * el formulario el control todavía no tiene padre.
   */
  private regla(campo: CampoEjecucion): (control: AbstractControl) => ValidationErrors | null {
    return (control) => {
      if (!control.parent) {
        return null;
      }
      const mensaje = this.errores[campo];
      return mensaje ? { regla: mensaje } : null;
    };
  }

  /**
   * Los límites de la fecha salen de la versión elegida; se recalculan solo
   * si cambia (el calendario recibe siempre el mismo objeto).
   */
  private actualizarLimites(): void {
    const limites = limitesDeFecha(this.versionElegida?.fechaCreacion, this.hoy);
    if (limites.desde === this.limites.desde && limites.hoy === this.limites.hoy && this.textoFechas) {
      return;
    }
    this.limites = limites;
    this.textoFechas = textoLimitesFecha(limites);
    this.fechaMinima = fechaLocalDeDia(limites.desde);
  }

  /** Una regla depende de otros campos (resultado y casos, versión y fecha): se revisan todos. */
  private revalidar(): void {
    Object.values(this.formulario.controls).forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }
}

/** «V3-13-08-2026 · Vigente» o «V2-16-07-2026 · Cerrada». */
function etiquetaVersion(version: VersionResumen): string {
  const nombre = version.nombre?.trim() || "Sin nombre";
  if (version.estado === "VIGENTE") {
    return `${nombre} · Vigente`;
  }
  return version.estado === "CERRADA" ? `${nombre} · Cerrada` : nombre;
}

/** Los ejecutores de la etapa más quien ejecutó la que se edita, si ya no está en ella. */
function ejecutoresConElGuardado(ejecutores: readonly Ejecutor[], ejecucion: EjecucionPrueba | null): Ejecutor[] {
  const lista = ejecutores.filter((ejecutor) => typeof ejecutor?.idEmpleado === "number");
  const id = ejecucion?.idEmpleadoEjecuto;
  if (typeof id === "number" && !lista.some((ejecutor) => ejecutor.idEmpleado === id)) {
    lista.push({ idEmpleado: id, nombre: ejecucion?.nombreEjecuto ?? null, rol: "Ya no está en la etapa" });
  }
  return lista;
}

function deIncidenciaVersion(incidencia: IncidenciaVersion): IncidenciaDeEtapa {
  return {
    id: incidencia.idIncidencia,
    folio: incidencia.folio,
    titulo: incidencia.titulo,
    prioridad: incidencia.prioridad,
    estatus: incidencia.estatus,
    version: incidencia.detectadaEn?.id ?? null,
  };
}

/** Una ligada que todavía no se puede nombrar: solo su id interno. */
function sinNombre(id: number): IncidenciaDeEtapa {
  return { id, folio: null, titulo: null, prioridad: null, estatus: null, version: null };
}
